import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq, gt, or } from 'ember-truth-helpers';
import ProfilePhoto from 'garaje/components/employees/profile-photo';
import LoadMore from 'garaje/components/load-more';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiSearch from 'garaje/components/ui-search';
import dateFormat from 'garaje/helpers/date-format';
import type MacAddressMappingModel from 'garaje/models/mac-address-mapping';
import clickOutside from 'garaje/modifiers/click-outside';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

import CsvUploadModal from './csv-upload-modal';
import MacAddressMappingsModal from './modal';
import SourcePrioritySettings, {
  MAC_SYNC_ON_INSTALL,
  PLUGIN_ID_TO_SOURCE,
  resolveSourceFromPlugin,
  type PriorityApiResponse,
  type SourcePriorityItem,
} from './source-priority-settings';

const PAGE_SIZE = 25;
const DATE_FORMAT_STR = 'MMM d, yyyy h:mm aa';
interface SourceOption {
  name: string;
  value: string;
}

/** Static sources always available regardless of plugins. */
const BASE_SOURCE_OPTIONS: SourceOption[] = [
  { name: 'CSV', value: 'csv' },
  { name: 'Manual', value: 'manual' },
];

/** All known sources for display name lookup. */
const ALL_SOURCE_DISPLAY: Record<string, string> = {
  csv: 'CSV',
  manual: 'Manual',
  jamf: 'Jamf',
  meraki: 'Cisco Meraki',
  aruba_central: 'Aruba Central',
  intune: 'Microsoft Intune',
};

function formatSource(source: string): string {
  return ALL_SOURCE_DISPLAY[source] ?? source;
}

interface MacAddressMappingsTableSignature {
  Args: {
    companyId: string;
    search: string;
    source: string;
    changeSearch: (search: string) => void;
    changeSource: (source: string) => void;
  };
}

export default class MacAddressMappingsTable extends Component<MacAddressMappingsTableSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare ajaxFetch: AjaxFetchService;

  @tracked entries: MacAddressMappingModel[] = [];
  @tracked totalCount = 0;
  @tracked companyMappingCount = 0;
  @tracked mappingInModal: MacAddressMappingModel | null = null;
  @tracked isCreating = false;
  @tracked mappingToDelete: MacAddressMappingModel | null = null;
  @tracked profilePictures: Record<string, string> = {};
  @tracked csvError = '';
  @tracked showCsvUploadModal = false;
  @tracked isSettingsPanelOpen = false;
  @tracked showAddMenu = false;
  @tracked sortField = 'last-synced-at';
  @tracked sortDirection: 'asc' | 'desc' = 'desc';
  @tracked showSourcePriorityModal = false;
  @tracked pluginSources: SourceOption[] = [];
  @tracked mappingSourceCounts: Record<string, number> = {};
  @tracked priorityConfig: PriorityApiResponse | null = null;

  /**
   * All dynamic sources: plugins with macSyncEnabled OR sources with existing mappings.
   * Deduped and excludes base sources (CSV, Manual).
   */
  get dynamicSources(): SourceOption[] {
    const baseValues = new Set(BASE_SOURCE_OPTIONS.map((s) => s.value));
    const seen = new Set<string>();
    const result: SourceOption[] = [];

    // Add plugin sources
    for (const s of this.pluginSources) {
      if (!baseValues.has(s.value) && !seen.has(s.value)) {
        result.push(s);
        seen.add(s.value);
      }
    }

    // Add sources that have existing mappings but no active plugin
    for (const [source, count] of Object.entries(this.mappingSourceCounts)) {
      if (count > 0 && !baseValues.has(source) && !seen.has(source)) {
        result.push({ name: formatSource(source), value: source });
        seen.add(source);
      }
    }

    return result;
  }

  /**
   * Source filter options: "All Sources" + static (CSV, Manual) + dynamic sources.
   */
  get sourceOptions(): SourceOption[] {
    return [{ name: 'All Sources', value: '' }, ...BASE_SOURCE_OPTIONS, ...this.dynamicSources];
  }

  /**
   * Sources available for priority configuration (excludes Manual — always highest).
   */
  get availableSourcesForPriority(): SourcePriorityItem[] {
    const sources: SourcePriorityItem[] = [{ source: 'csv', name: 'CSV', priority: 1 }];
    for (const s of this.dynamicSources) {
      sources.push({ source: s.value, name: s.name, priority: 1 });
    }
    return sources;
  }

  /**
   * Compute display names of sources with higher priority than CSV.
   * Used to warn users in the CSV upload modal.
   */
  get higherPrioritySourcesForCsv(): string[] {
    const config = this.priorityConfig;
    if (!config?.enabled || config.priorities.length === 0) return [];

    const csvPriority = config.priorities.find((p) => p.source === 'csv')?.priority;
    if (csvPriority == null) return [];

    // Only show sources that are actually present in the filter/priority list
    const relevantSources = new Set(this.dynamicSources.map((s) => s.value));

    return config.priorities
      .filter((p) => p.source !== 'csv' && p.priority < csvPriority && relevantSources.has(p.source))
      .map((p) => formatSource(p.source));
  }

  page = 0;

  private hasLoadedOnce = false;

  get sortParam(): string {
    return this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField;
  }

  get searchQuery(): string {
    return this.args.search ?? '';
  }

  get selectedSourceOption(): SourceOption {
    const value = this.args.source ?? '';
    return this.sourceOptions.find((o) => o.value === value) ?? this.sourceOptions[0]!;
  }

  get sourceFilter(): string {
    return this.args.source ?? '';
  }

  get hasMorePages(): boolean {
    return this.entries.length < this.totalCount;
  }

  resetTable(): void {
    this.page = 0;
  }

  loadData = restartableTask(async () => {
    try {
      const isFirstPage = this.page === 0;
      const nextPage = this.page + 1;
      const offset = (nextPage - 1) * PAGE_SIZE;

      const queryParams: Record<string, unknown> = {
        sort: this.sortParam,
        page: {
          offset,
          limit: PAGE_SIZE,
        },
      };

      if (this.sourceFilter) {
        queryParams['source'] = this.sourceFilter;
      }

      if (this.searchQuery) {
        queryParams['query'] = this.searchQuery;
      }

      const result = await this.store.query('mac-address-mapping', queryParams);
      const meta = (result as unknown as { meta?: { total?: number } }).meta;
      const newResults = result.slice();

      this.page = nextPage;

      // Replace entries on first page, append on subsequent pages
      this.entries = isFirstPage ? newResults : [...this.entries, ...newResults];

      // Use total from API meta if available
      if (meta?.total != null) {
        this.totalCount = meta.total;
        // Set company-wide count on first unfiltered load
        if (!this.hasLoadedOnce && !this.sourceFilter && !this.searchQuery) {
          this.companyMappingCount = meta.total;
          this.hasLoadedOnce = true;
          // Load dynamic sources and priority config on first load
          void this.loadPluginSourcesAndPriority.perform();
        }
      } else if (newResults.length < PAGE_SIZE) {
        this.totalCount = this.entries.length;
      } else {
        this.totalCount = this.entries.length + 1;
      }

      // Load employee profile pictures for newly fetched entries
      const existingPictures = isFirstPage ? {} : this.profilePictures;
      const newEmployeeIds = [
        ...new Set(newResults.map((m) => m.employeeId).filter((id) => id && !existingPictures[String(id)])),
      ];
      if (newEmployeeIds.length > 0) {
        const pictures: Record<string, string> = { ...existingPictures };
        await Promise.all(
          newEmployeeIds.map(async (id) => {
            try {
              const employee = await this.store.findRecord('employee', String(id), {
                backgroundReload: false,
              });
              if (employee?.profilePictureUrl) {
                pictures[String(id)] = employee.profilePictureUrl;
              }
            } catch {
              // Employee not found or not accessible — use default avatar
            }
          }),
        );
        this.profilePictures = pictures;
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error loading MAC address mappings', {
        details: parseErrorForDisplay(error),
      });
      this.entries = [];
      this.totalCount = 0;
    }
  });

  searchTask = restartableTask(async () => {
    await timeout(300);
    this.resetTable();
    void this.loadData.perform();
  });

  deleteMapping = dropTask(async () => {
    const mapping = this.mappingToDelete;
    if (!mapping) return;

    try {
      const url = urlBuilder.macAddressMappings.delete();
      await this.ajaxFetch.request(url, {
        type: 'DELETE',
        contentType: 'application/json',
        data: JSON.stringify({
          companyId: parseInt(this.args.companyId, 10),
          macAddresses: [mapping.macAddress],
        }),
      });
      this.flashMessages.showAndHideFlash('success', 'Mapping deleted');
      this.companyMappingCount = Math.max(0, this.companyMappingCount - 1);
      this.mappingToDelete = null;
      this.resetTable();
      void this.loadData.perform();
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error deleting mapping', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  saveMapping = dropTask(
    async (data: { macAddress: string; email: string; employeeId?: string; employeeName?: string }) => {
      try {
        const isEditing = this.mappingInModal !== null;
        const mapping: Record<string, unknown> = {
          macAddress: data.macAddress,
          email: data.email,
          source: 'manual',
        };
        if (data.employeeId) mapping['employeeId'] = parseInt(data.employeeId, 10);
        if (data.employeeName) mapping['name'] = data.employeeName;

        if (isEditing) {
          const originalMac = this.mappingInModal!.macAddress;
          const url = urlBuilder.macAddressMappings.getByMacAddress(originalMac);
          await this.ajaxFetch.request(url, {
            type: 'PATCH',
            contentType: 'application/json',
            data: JSON.stringify({ ...mapping, companyId: parseInt(this.args.companyId, 10) }),
          });
        } else {
          const url = urlBuilder.macAddressMappings.sync();
          await this.ajaxFetch.request(url, {
            type: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({
              companyId: parseInt(this.args.companyId, 10),
              source: 'manual',
              mappings: [mapping],
            }),
          });
        }
        this.flashMessages.showAndHideFlash('success', 'Mapping saved');
        if (!isEditing) {
          this.companyMappingCount += 1;
        }
        this.mappingInModal = null;
        this.isCreating = false;
        this.resetTable();
        void this.loadData.perform();
      } catch (error) {
        this.flashMessages.showAndHideFlash('error', 'Error saving mapping', {
          details: parseErrorForDisplay(error),
        });
      }
    },
  );

  @action
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.args.changeSearch(target.value);
    void this.searchTask.perform();
  }

  @action
  onSearchClear(): void {
    this.args.changeSearch('');
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  sortAction(field: string, direction: 'asc' | 'desc'): void {
    this.sortField = field;
    this.sortDirection = direction;
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  onSourceFilterChange(option: SourceOption): void {
    this.args.changeSource(option.value);
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  loadMore(): void {
    if (this.loadData.isRunning) return;
    if (this.hasMorePages) {
      void this.loadData.perform();
    }
  }

  @action
  openCreateModal(): void {
    this.mappingInModal = null;
    this.isCreating = true;
  }

  @action
  openEditModal(mapping: MacAddressMappingModel, event?: Event): void {
    event?.stopPropagation();
    this.mappingInModal = mapping;
    this.isCreating = false;
  }

  @action
  closeModal(): void {
    this.mappingInModal = null;
    this.isCreating = false;
  }

  @action
  openDeleteConfirmation(mapping: MacAddressMappingModel): void {
    this.mappingToDelete = mapping;
  }

  @action
  stopPropAndDelete(mapping: MacAddressMappingModel, event: Event): void {
    event.stopPropagation();
    this.mappingToDelete = mapping;
  }

  exportMappings = dropTask(async () => {
    try {
      const allMappings: MacAddressMappingModel[] = [];
      const exportPageSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const result = await this.store.query('mac-address-mapping', {
          page: { offset, limit: exportPageSize },
        });
        const page = result.slice();
        allMappings.push(...page);
        offset += exportPageSize;
        hasMore = page.length === exportPageSize;
      }

      const rows = ['email,mac_address'];
      for (const mapping of allMappings) {
        const email = mapping.employeeEmail ?? '';
        const mac = mapping.macAddress ?? '';
        rows.push(`${email},${mac}`);
      }

      const csv = rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const anchor = document.createElement('a');
      anchor.download = 'mac-address-mappings.csv';
      anchor.href = URL.createObjectURL(blob);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      this.flashMessages.showAndHideFlash('success', 'Export downloaded');
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error exporting mappings', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  uploadCsv = dropTask(async (file: File) => {
    this.showCsvUploadModal = false;

    try {
      const url = urlBuilder.macAddressMappings.syncCsv();
      const formData = new FormData();
      formData.append('companyId', this.args.companyId);
      formData.append('file', file);

      const response = await this.ajaxFetch.request(url, {
        type: 'POST',
        contentType: false,
        processData: false,
        data: formData,
      });

      const jobId = (response as { jobId: string }).jobId;
      if (jobId) {
        await this.pollCsvJob(jobId);
      } else {
        this.flashMessages.showAndHideFlash('success', 'CSV uploaded successfully');
        this.hasLoadedOnce = false;
        this.resetTable();
        void this.loadData.perform();
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error uploading CSV', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  async pollCsvJob(jobId: string): Promise<void> {
    const url = urlBuilder.macAddressMappings.syncCsvJob(jobId);
    const maxAttempts = 60;

    for (let i = 0; i < maxAttempts; i++) {
      await timeout(2000);

      const job: { status: string; errorMessage?: string | null } = await this.ajaxFetch.request(url, {
        type: 'GET',
      });

      if (job.status === 'completed') {
        this.flashMessages.showAndHideFlash('success', 'CSV sync completed');
        this.hasLoadedOnce = false;
        this.resetTable();
        void this.loadData.perform();
        return;
      }

      if (job.status === 'failed') {
        this.flashMessages.showAndHideFlash('error', 'CSV sync failed', {
          details: job.errorMessage ?? 'Unknown error',
        });
        return;
      }
    }

    this.flashMessages.showAndHideFlash('warning', 'CSV sync is still processing. Please refresh later.');
  }

  @action
  toggleAddMenu(): void {
    this.showAddMenu = !this.showAddMenu;
  }

  @action
  closeAddMenu(): void {
    this.showAddMenu = false;
  }

  @action
  addSingleRecord(): void {
    this.showAddMenu = false;
    this.openCreateModal();
  }

  @action
  importFromCsv(): void {
    this.showAddMenu = false;
    this.showCsvUploadModal = true;
  }

  @action
  closeCsvUploadModal(): void {
    this.showCsvUploadModal = false;
  }

  @action
  openSourcePriorityModal(): void {
    this.showSourcePriorityModal = true;
  }

  @action
  onPrioritySaved(config: PriorityApiResponse): void {
    this.priorityConfig = config;
  }

  @action
  closeSourcePriorityModal(): void {
    this.showSourcePriorityModal = false;
  }

  @action
  showSettings(): void {
    this.isSettingsPanelOpen = true;
    if (!this.hasLoadedOnce) {
      void this.loadData.perform();
    }
  }

  private hasLoadedPlugins = false;

  loadPluginSourcesAndPriority = dropTask(async () => {
    try {
      const companyId = this.args.companyId;
      if (!companyId) return;

      // Load plugin installs once — these don't change within a session
      if (!this.hasLoadedPlugins) {
        const macSyncPluginIds = Object.keys(PLUGIN_ID_TO_SOURCE);
        const pluginInstalls = await this.store.query('plugin-install', {
          filter: { company: companyId, plugins: macSyncPluginIds },
          page: { limit: 100 },
        });

        const dynamicSources: SourceOption[] = [];
        const seen = new Set<string>();
        for (const install of pluginInstalls.slice()) {
          if (install.status !== 'active' && install.status !== 'pending') continue;
          const config = install.config as Record<string, unknown> | undefined;
          const plugin = await install.plugin;
          if (!plugin) continue;
          if (!config?.['macSyncEnabled'] && !MAC_SYNC_ON_INSTALL.has(plugin.id)) continue;

          const sourceInfo = resolveSourceFromPlugin(plugin.id);
          if (sourceInfo && !seen.has(sourceInfo.value)) {
            dynamicSources.push({ name: sourceInfo.name, value: sourceInfo.value });
            seen.add(sourceInfo.value);
          }
        }

        this.pluginSources = dynamicSources;
        this.hasLoadedPlugins = true;
      }

      // Only check mapping counts for sources not already found via plugins
      const pluginSourceValues = new Set(this.pluginSources.map((s) => s.value));
      const integrationSources = ['jamf', 'meraki', 'aruba_central', 'intune'];
      const sourcesToCheck = integrationSources.filter((s) => !pluginSourceValues.has(s));
      const counts: Record<string, number> = {};
      if (sourcesToCheck.length > 0) {
        await Promise.all(
          sourcesToCheck.map(async (source) => {
            const result = await this.store.query('mac-address-mapping', {
              source,
              page: { limit: 1, offset: 0 },
            });
            const meta = (result as unknown as { meta?: { total?: number } }).meta;
            counts[source] = meta?.total ?? result.slice().length;
          }),
        );
      }
      this.mappingSourceCounts = counts;

      // Always refresh priority config — it may have changed via the settings modal
      // eslint-disable-next-line ember/use-ember-get-and-set
      const url = urlBuilder.macAddressSourcePriorities.get(companyId);
      this.priorityConfig = await this.ajaxFetch.request(url, { type: 'GET' });
    } catch {
      // Non-fatal — priority features just won't show dynamic data
    }
  });

  @action
  hideSettings(): void {
    this.isSettingsPanelOpen = false;
  }

  @action
  closeDeleteConfirmation(): void {
    this.mappingToDelete = null;
  }

  <template>
    <SettingsPanel
      @isExpandable={{true}}
      @isOpen={{this.isSettingsPanelOpen}}
      @onCancel={{this.hideSettings}}
      data-test-mac-mappings-panel
      as |sp|
    >
      <sp.header
        @iconUri="/assets/images/features/directory.svg"
        @title="MAC address mappings"
        @description="Manage MAC address to employee mappings for presence detection."
        as |header|
      >
        <header.controls>
          {{#unless this.isSettingsPanelOpen}}
            <button
              type="button"
              class="btn btn--small btn--brand"
              data-test-mac-mappings-edit-btn
              {{on "click" this.showSettings}}
            >
              Edit
            </button>
          {{/unless}}
        </header.controls>
      </sp.header>
      <sp.body>
        <div class="mt-4">
          <div class="flex items-center justify-between pl-2 mb-4" data-test-mac-mappings-toolbar>
            <div class="flex items-center gap-2">
              <PowerSelect
                class="!mb-0"
                @dropdownClass="powerSelect-radio"
                @selected={{this.selectedSourceOption}}
                @options={{this.sourceOptions}}
                @searchEnabled={{false}}
                @onChange={{this.onSourceFilterChange}}
                data-test-mac-mappings-source-filter
                as |option|
              >
                {{option.name}}
              </PowerSelect>
              <button
                type="button"
                class="btn btn--small btn--outline !inline-flex items-center justify-center !px-2 !min-h-[38px] !w-[38px]"
                title="Source priority settings"
                data-test-source-priority-btn
                {{on "click" this.openSourcePriorityModal}}
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn btn--outline btn--small"
                data-test-mac-mappings-export-btn
                disabled={{this.exportMappings.isRunning}}
                {{on "click" this.exportMappings.perform}}
              >
                {{if this.exportMappings.isRunning "Exporting..." "Export all"}}
              </button>
              <div class="relative" data-test-mac-mappings-add-dropdown {{clickOutside this.closeAddMenu}}>
                <button
                  type="button"
                  class="btn btn--brand btn--small !inline-flex items-center gap-1"
                  disabled={{this.uploadCsv.isRunning}}
                  data-test-mac-mappings-add-btn
                  {{on "click" this.toggleAddMenu}}
                >
                  {{#if this.uploadCsv.isRunning}}
                    <img src="/assets/images/progress-small-gray.svg" alt="" class="w-5 h-5 mr-1" />
                    Syncing...
                  {{else}}
                    Add mappings
                    <svg class="w-3 h-3 ml-1" viewBox="0 0 12 12" fill="currentColor"><path d="M2 4l4 4 4-4" /></svg>
                  {{/if}}
                </button>
                {{#if this.showAddMenu}}
                  <div
                    class="absolute right-0 mt-1 w-48 bg-white rounded-[8px] shadow-lg border border-carbon-10 z-10 py-1"
                    data-test-add-menu
                  >
                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm hover:bg-gem-5 hover:text-gem-60"
                      data-test-add-single-record
                      {{on "click" this.addSingleRecord}}
                    >
                      Add a single mapping
                    </button>
                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm hover:bg-gem-5 hover:text-gem-60"
                      disabled={{this.uploadCsv.isRunning}}
                      data-test-import-from-csv
                      {{on "click" this.importFromCsv}}
                    >
                      {{if this.uploadCsv.isRunning "Syncing..." "Import from CSV"}}
                    </button>
                  </div>
                {{/if}}
              </div>
            </div>
          </div>

          <div class="flex items-center w-full mb-4 pl-2">
            <div class="flex items-center gap-4 flex-auto mt-4">
              <span class="text-sm text-carbon-60">
                {{this.totalCount}}
                {{if (eq this.totalCount 1) "mapping" "mappings"}}{{#if this.searchQuery}}
                  matching "{{this.searchQuery}}"{{/if}}
              </span>
            </div>

            <UiSearch
              @placeholder="Search by name or MAC address"
              @onInput={{this.onSearchInput}}
              @value={{this.searchQuery}}
              @showClearButton={{if this.searchQuery true false}}
              @onClear={{this.onSearchClear}}
              style="width: 350px;"
              data-test-mac-mappings-search
            />
          </div>

          {{#if (gt this.entries.length 0)}}
            <div class="max-h-[600px] overflow-y-auto" data-test-mac-mappings-scroll-container>
              <div class="table w-full rounded text-carbon-50" data-test-mac-mappings-table>
                <div class="table-row">
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 pl-5 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    <SortableHeaderColumn
                      @name="Employee name"
                      @sortField="employee-name"
                      @sortAction={{this.sortAction}}
                      @isActive={{eq this.sortField "employee-name"}}
                      @sortDirection={{this.sortDirection}}
                    />
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    MAC address
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    Source
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    <SortableHeaderColumn
                      @name="Last synced"
                      @sortField="last-synced-at"
                      @sortAction={{this.sortAction}}
                      @isActive={{eq this.sortField "last-synced-at"}}
                      @sortDirection={{this.sortDirection}}
                    />
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                  </div>
                </div>

                {{#each this.entries as |mapping|}}
                  <div class="table-row hover:bg-carbon-5 h-16" data-test-mac-mapping-row>
                    <div
                      class="table-cell border-b p-3 pl-5 px-4 align-middle text-sm text-carbon-70"
                      data-test-employee-name
                    >
                      <div class="flex items-center gap-3">
                        <div class="uppercase shrink-0">
                          <ProfilePhoto
                            @id={{concat "" mapping.employeeId}}
                            @name={{mapping.employeeName}}
                            @devicePhoto={{get this.profilePictures (concat "" mapping.employeeId)}}
                            @isDisabled={{true}}
                            @size={{3}}
                          />
                        </div>
                        <LinkTo
                          @route="employees.directory.employee"
                          @model={{mapping.employeeId}}
                          class="font-medium text-carbon-70 hover:underline"
                        >
                          {{mapping.employeeName}}
                        </LinkTo>
                      </div>
                    </div>
                    <div
                      class="table-cell border-b p-3 px-4 align-middle text-sm font-mono text-carbon-70"
                      data-test-mac-address
                    >
                      {{mapping.macAddress}}
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm text-carbon-50" data-test-source>
                      <span class="inline-block rounded bg-carbon-10 px-2 py-0.5 text-xs">
                        {{formatSource mapping.source}}
                      </span>
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm text-carbon-50" data-test-last-synced>
                      {{#if mapping.lastSyncedAt}}
                        {{dateFormat mapping.lastSyncedAt DATE_FORMAT_STR}}
                      {{else}}
                        —
                      {{/if}}
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm">
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Edit"
                          data-test-edit-mapping
                          {{on "click" (fn this.openEditModal mapping)}}
                        >
                          <img src="/assets/images/edit-16.svg" alt="edit" class="block w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Delete"
                          data-test-delete-mapping
                          {{on "click" (fn this.stopPropAndDelete mapping)}}
                        >
                          <img src="/assets/images/trash-16.svg" alt="delete" class="block w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                {{/each}}
              </div>

              <LoadMore
                class="load-more m-4"
                @hasMorePages={{this.hasMorePages}}
                @useWindow={{false}}
                @action={{this.loadMore}}
              />
            </div>
          {{else if this.loadData.isRunning}}
            <LoadingSpinnerPulse data-test-loading-spinner class="pt-12" />
          {{else}}
            <div class="flex justify-center items-center py-12" data-test-empty-state>
              <div class="text-center text-base text-carbon-50">
                No MAC address mappings found
              </div>
            </div>
          {{/if}}
        </div>
      </sp.body>
    </SettingsPanel>

    {{#if (or this.isCreating this.mappingInModal)}}
      <MacAddressMappingsModal
        @mapping={{this.mappingInModal}}
        @onSave={{this.saveMapping.perform}}
        @onClose={{this.closeModal}}
        @isSaving={{this.saveMapping.isRunning}}
        @companyId={{@companyId}}
      />
    {{/if}}

    {{#if this.mappingToDelete}}
      <ConfirmationModal
        @title="Delete mapping"
        @ctaText="Delete"
        @handleTaskAction={{this.deleteMapping}}
        @onClose={{this.closeDeleteConfirmation}}
      >
        <p class="text-sm text-carbon-60">
          Are you sure you want to delete the mapping for:
        </p>
        <p class="mt-2 text-sm text-carbon-70">
          <strong>{{this.mappingToDelete.employeeName}}</strong>
          —
          <strong>{{this.mappingToDelete.macAddress}}</strong>
        </p>
      </ConfirmationModal>
    {{/if}}

    {{#if this.showCsvUploadModal}}
      <CsvUploadModal
        @onUpload={{this.uploadCsv.perform}}
        @onClose={{this.closeCsvUploadModal}}
        @isUploading={{this.uploadCsv.isRunning}}
        @currentMappingCount={{this.companyMappingCount}}
        @higherPrioritySources={{this.higherPrioritySourcesForCsv}}
      />
    {{/if}}

    {{#if this.showSourcePriorityModal}}
      <SourcePrioritySettings
        @companyId={{@companyId}}
        @availableSources={{this.availableSourcesForPriority}}
        @onClose={{this.closeSourcePriorityModal}}
        @onSave={{this.onPrioritySaved}}
      />
    {{/if}}
  </template>
}
